/**
 * API Client for communicating with backend API Gateway
 * This replaces direct AWS SDK calls for better security
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  details?: any;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    if (!this.baseUrl) {
      return {
        error: 'API URL not configured. Set REACT_APP_API_URL environment variable.',
      };
    }

    try {
      // Get authentication token if available (for Cognito)
      let authToken: string | null = null;
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        authToken = session.tokens?.idToken?.toString() || null;
      } catch {
        // Cognito not configured or not authenticated, continue without token
      }

      const url = `${this.baseUrl}${endpoint}`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // Add authorization header if token is available
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      let data: any;
      try {
        data = await response.json();
      } catch (parseError) {
        // If response is not JSON, create a meaningful error
        const text = await response.text();
        return {
          error: `Server error (${response.status}): ${text || response.statusText || 'Unknown error'}`,
          details: { status: response.status, statusText: response.statusText },
        };
      }

      if (!response.ok) {
        // Use detailed error message from backend if available
        const errorMessage = data.error || data.message || `Request failed with status ${response.status}`;
        
        // For 404 errors that are handled gracefully (like file not found),
        // don't log as error in console - these are expected in some cases
        const isHandled404 = response.status === 404 && (
          errorMessage.includes('not found') || 
          errorMessage.includes('NoSuchKey') ||
          errorMessage.includes('file not found') ||
          errorMessage.includes('Document file not found') ||
          endpoint.includes('/move-file') ||
          endpoint.includes('/url')  // Presigned URL requests for missing files
        );
        
        // Suppress console errors for expected 404s (missing files are handled gracefully)
        if (!isHandled404) {
          if (process.env.NODE_ENV === 'development') {
            // Only log unexpected errors in development
            console.warn(`API Error (${response.status}): ${errorMessage}`);
          }
        }
        
        return {
          error: errorMessage,
          details: {
            ...data.details,
            status: response.status,
            statusText: response.statusText,
            originalError: data.error,
            message: data.message,
          },
        };
      }

      return { data };
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return { error: 'aborted', details: error };
      }
      return {
        error: error.message || 'Network error',
        details: error,
      };
    }
  }

  async get<T>(
    endpoint: string,
    init?: Pick<RequestInit, 'signal'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', ...init });
  }

  async post<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// API endpoints
export const apiEndpoints = {
  documents: {
    list: (params?: {
      status?: string;
      search?: string;
      limit?: number;
      nextToken?: string;
      fieldFilters?: Record<string, string>;
      dateFrom?: string;
      dateTo?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken);
      if (params?.fieldFilters && Object.keys(params.fieldFilters).length > 0) {
        queryParams.append('fieldFilters', JSON.stringify(params.fieldFilters));
      }
      if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
      const query = queryParams.toString();
      return `/apis/v1/documents${query ? `?${query}` : ''}`;
    },
    create: () => `/apis/v1/documents`,
    get: (id: string) => `/apis/v1/documents/${id}`,
    update: (id: string) => `/apis/v1/documents/${id}`,
    delete: (id: string, permanent: boolean = false) => 
      `/apis/v1/documents/${id}${permanent ? '?permanent=true' : ''}`,
    upload: () => `/apis/v1/documents/upload`,
    uploadUrl: () => `/apis/v1/documents/upload-url`,
    duplicateCheck: () => `/apis/v1/documents/duplicate-check`,
    process: (id: string) => `/apis/v1/documents/${id}/process`,
    textractStatus: (id: string) => `/apis/v1/documents/${id}/textract-status`,
    approve: (id: string) => `/apis/v1/documents/${id}/approve`,
    getUrl: (id: string, expiresIn?: number) => 
      `/apis/v1/documents/${id}/url${expiresIn ? `?expiresIn=${expiresIn}` : ''}`,
    moveFile: (id: string) => `/apis/v1/documents/${id}/move-file`,
    storageExists: (id: string, params: { folder: 'pending' | 'active' | 'deleted'; kind?: 'original' | 'thumbnail' }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('folder', params.folder);
      if (params.kind) queryParams.append('kind', params.kind);
      const query = queryParams.toString();
      return `/apis/v1/documents/${id}/storage-exists?${query}`;
    },
    restore: (id: string) => `/apis/v1/documents/${id}/restore`,
    abortIncompleteUpload: (id: string) => `/apis/v1/documents/${id}/abort-incomplete-upload`,
  },
  templates: {
    list: () => `/apis/v1/templates`,
    get: (id: string) => `/apis/v1/templates/${id}`,
    create: () => `/apis/v1/templates`,
    update: (id: string) => `/apis/v1/templates/${id}`,
    delete: (id: string) => `/apis/v1/templates/${id}`,
  },
  cardView: {
    get: () => `/apis/v1/card-view-config`,
    update: () => `/apis/v1/card-view-config`,
  },
  stats: {
    get: () => `/apis/v1/stats`,
  },
  admin: {
    dashboardAnalytics: (params?: { startDate?: string; endDate?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      const query = queryParams.toString();
      return `/apis/v1/admin/dashboard-analytics${query ? `?${query}` : ''}`;
    },
  },
  oneTimeActions: {
    list: () => `/apis/v1/admin/one-time-actions`,
    init: () => `/apis/v1/admin/one-time-actions/init`,
    start: (actionId: string) => `/apis/v1/admin/one-time-actions/${actionId}/start`,
    stop: (actionId: string) => `/apis/v1/admin/one-time-actions/${actionId}/stop`,
    status: (actionId: string) => `/apis/v1/admin/one-time-actions/${actionId}/status`,
    history: (actionId: string) => `/apis/v1/admin/one-time-actions/${actionId}/history`,
  },
  logging: {
    client: () => `/apis/v1/logs/client`,
    getConfig: () => `/apis/v1/admin/logging-config`,
    updateConfig: () => `/apis/v1/admin/logging-config`,
    listRecent: (params?: { category?: 'admin' | 'user'; limit?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.category) queryParams.append('category', params.category);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      const query = queryParams.toString();
      return `/apis/v1/admin/logs${query ? `?${query}` : ''}`;
    },
  },
  awsLogs: {
    listGroups: (params?: { prefix?: string; nextToken?: string; limit?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.prefix) queryParams.append('prefix', params.prefix);
      if (params?.nextToken) queryParams.append('nextToken', params.nextToken);
      if (params?.limit != null) queryParams.append('limit', params.limit.toString());
      const query = queryParams.toString();
      return `/apis/v1/admin/aws-log-groups${query ? `?${query}` : ''}`;
    },
    filterEvents: (params: {
      logGroupName: string;
      startTimeMs?: number;
      endTimeMs?: number;
      filterPattern?: string;
      limit?: number;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('logGroupName', params.logGroupName);
      if (params.startTimeMs != null) queryParams.append('startTimeMs', String(params.startTimeMs));
      if (params.endTimeMs != null) queryParams.append('endTimeMs', String(params.endTimeMs));
      if (params.filterPattern) queryParams.append('filterPattern', params.filterPattern);
      if (params.limit != null) queryParams.append('limit', String(params.limit));
      return `/apis/v1/admin/aws-log-events?${queryParams.toString()}`;
    },
  },
  searchFilterControls: {
    getConfig: () => `/apis/v1/admin/search-filter-controls`,
    updateConfig: () => `/apis/v1/admin/search-filter-controls`,
  },
  issues: {
    list: (params?: {
      issueStatus?: string;
      priority?: string;
      assignedTo?: string;
      submittedBy?: string;
      createdFrom?: string;
      createdTo?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.issueStatus) queryParams.append('issueStatus', params.issueStatus);
      if (params?.priority) queryParams.append('priority', params.priority);
      if (params?.assignedTo) queryParams.append('assignedTo', params.assignedTo);
      if (params?.submittedBy) queryParams.append('submittedBy', params.submittedBy);
      if (params?.createdFrom) queryParams.append('createdFrom', params.createdFrom);
      if (params?.createdTo) queryParams.append('createdTo', params.createdTo);
      const query = queryParams.toString();
      return `/apis/v1/issues${query ? `?${query}` : ''}`;
    },
    get: (id: string) => `/apis/v1/issues/${id}`,
    create: () => `/apis/v1/issues`,
    update: (id: string) => `/apis/v1/issues/${id}`,
  },
};

